import { PrivateRoute } from "app";
import { SuspenseFallback } from "common/components/LoadingScreen";
import AnimalGroupDetail from "features/animalGroupDetail/AnimalGroupDetail";
import AnimalGroupSetupWizard from "features/animalGroupDetail/AnimalGroupSetupWizard";
import { FieldInput, FieldView } from "features/fieldDetail";
import { useUserFarms } from "model";
import { MutableRefObject, Suspense, useEffect } from "react";
import { MapRef } from "react-map-gl/mapbox";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { useAppStore } from "stores";
import FarmBottomSheet from "./FarmBottomSheet";
import { FarmInput } from "./FarmInput";
import FarmOverviewContent from "./FarmOverviewContent";
import FieldsListScreen from "./FieldsListScreen";

interface FarmBottomSheetRouterProps {
  mapRef: MutableRefObject<MapRef | null>;
}

export default function FarmBottomSheetRouter({
  mapRef,
}: FarmBottomSheetRouterProps) {
  const location = useLocation();
  const navigate = useNavigate();
  // Use proper React Query hook and Zustand for auth/farms state
  const { data: allFarms, isLoading: farmsLoading } = useUserFarms();
  const isLoggedIn = useAppStore((state) => state.isAuthenticated);
  const currentFarmId = useAppStore((state) => state.currentFarmId);

  // Redirect /farm or /farm/ (no ID) to the current farm or add-farm
  useEffect(() => {
    const isExactFarmPath =
      location.pathname === "/farm" || location.pathname === "/farm/";

    if (isLoggedIn && isExactFarmPath) {
      if (currentFarmId) {
        navigate(`/farm/${currentFarmId}`, { replace: true });
      } else if (allFarms.length > 0) {
        navigate(`/farm/${allFarms[0].guid}`, { replace: true });
      } else if (!farmsLoading) {
        navigate("/add-farm/enter-name", { replace: true });
      }
      return;
    }

    // Redirect to add-farm if user has no farms and tries to access farm routes
    const isFarmRoute = location.pathname.startsWith("/farm/");
    const hasFarms = allFarms.length > 0;
    const isLoading = farmsLoading;

    if (isLoggedIn && isFarmRoute && !hasFarms && !isLoading) {
      navigate("/add-farm/enter-name", { replace: true });
    }
  }, [
    location.pathname,
    allFarms,
    farmsLoading,
    isLoggedIn,
    navigate,
    currentFarmId,
  ]);

  // Check if we're on a farm-related route that should use the bottom sheet
  // Note: add-field and edit-field routes use their own full-screen dialog pattern
  // so they are rendered separately (not inside the bottom sheet)
  const isFarmTabRoute =
    location.pathname === "/farm" ||
    location.pathname === "/farm/" ||
    location.pathname.startsWith("/farm/") ||
    location.pathname.startsWith("/field/") ||
    location.pathname.startsWith("/fields-list/") ||
    location.pathname.startsWith("/animal-group/") ||
    location.pathname.startsWith("/add-animal-group");

  // Routes that should render on top of the bottom sheet but not inside it
  const isOverlayRoute =
    location.pathname.startsWith("/add-field") ||
    location.pathname.startsWith("/edit-field") ||
    location.pathname.startsWith("/add-farm") ||
    location.pathname.startsWith("/add-animal-group");

  // Render overlay routes (these have their own full-screen dialogs)
  if (isOverlayRoute) {
    return (
      <Suspense fallback={<SuspenseFallback />}>
        <Routes>
          <Route
            path="/add-farm/*"
            element={
              <PrivateRoute>
                <FarmInput mapRef={mapRef} />
              </PrivateRoute>
            }
          />
          <Route
            path="/edit-field/:fieldId/*"
            element={
              <PrivateRoute>
                <FieldInput />
              </PrivateRoute>
            }
          />
          <Route
            path="/add-field/*"
            element={
              <PrivateRoute>
                <FieldInput />
              </PrivateRoute>
            }
          />
          <Route
            path="/add-animal-group"
            element={
              <PrivateRoute>
                <AnimalGroupSetupWizard />
              </PrivateRoute>
            }
          />
          {/* Catch-all for unmatched paths */}
          <Route path="*" element={null} />
        </Routes>
      </Suspense>
    );
  }

  if (!isFarmTabRoute) {
    return null;
  }

  return (
    <FarmBottomSheet>
      <Suspense fallback={<SuspenseFallback />}>
        <Routes>
          <Route
            path="/farm/:farmId"
            element={
              <PrivateRoute>
                <FarmOverviewContent />
              </PrivateRoute>
            }
          />
          <Route
            path="/fields-list/:fieldType"
            element={
              <PrivateRoute>
                <FieldsListScreen />
              </PrivateRoute>
            }
          />
          <Route
            path="/field/:fieldId"
            element={
              <PrivateRoute>
                <FieldView />
              </PrivateRoute>
            }
          />
          <Route
            path="/animal-group/:animalGroupId/*"
            element={
              <PrivateRoute>
                <AnimalGroupDetail />
              </PrivateRoute>
            }
          />
          {/* Catch-all for unmatched paths */}
          <Route path="*" element={null} />
        </Routes>
      </Suspense>
    </FarmBottomSheet>
  );
}
